/* eslint-disable react-hooks/exhaustive-deps */
import styled from "@emotion/styled";
import { useCallback, useMemo, useState } from "react";

import {
  ContextMenu,
  MenuItem,
  PropertiesPanelSection,
  SkiliticsIcon,
  ToggleButton,
  Tooltip,
} from "@skilitics/designer-ui-components";
import { clearAllInsetProperties, type SliderDirection } from "@skilitics/drag-drop";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { AbsolutePositionForm } from "./AbsolutePositionForm";
import { useEditingCss } from "./EditingCssPropertiesContext";
import { LayoutSettingsForm } from "./LayoutSettingsForm";
import { SizeForm } from "./SizeForm";

const StyledToggleButton = styled(ToggleButton)`
  margin-left: 10px;
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius}px !important;
  height: 28px;
  width: 28px;
  font-size: 15px;
  padding: 0;
  &[disabled] {
    border: 0;
  }
`;

type Props = {
  enableFitContent: boolean;
  sliderDirection?: SliderDirection;
};

type Restriction = "minimum" | "maximum";
type RestrictionUpdate = "add" | "remove";

export const LayoutProperties = ({ enableFitContent, sliderDirection }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const { cssProperties, onCssPropertiesChange, layoutSettings, onLayoutSettingsChange, onChange } = useEditingCss();

  const [useMinimums, setUseMinimums] = useState(!!(cssProperties.minWidth || cssProperties.minHeight));
  const [useMaximums, setUseMaximums] = useState(!!(cssProperties.maxWidth || cssProperties.maxHeight));

  const createHandleUpdateRestriction = useCallback(
    (restriction: Restriction, update: RestrictionUpdate) => {
      const addRestriction = () => {
        if (restriction === "minimum") {
          onCssPropertiesChange({ minHeight: "0px", minWidth: "0px" });
        } else {
          onCssPropertiesChange({ maxHeight: "100%", maxWidth: "100%" });
        }
      };

      const removeRestriction = () => {
        if (restriction === "minimum") {
          onCssPropertiesChange({ minHeight: undefined, minWidth: undefined });
        } else {
          onCssPropertiesChange({ maxHeight: undefined, maxWidth: undefined });
        }
      };

      return () => {
        if (restriction === "minimum") {
          setUseMinimums(update === "add");
        } else {
          setUseMaximums(update === "add");
        }

        if (update === "add") {
          addRestriction();
        } else {
          removeRestriction();
        }
      };
    },
    [onCssPropertiesChange],
  );

  const menuItems = useMemo(() => {
    return [
      useMinimums ? (
        <MenuItem key="removeMinimums" onClick={createHandleUpdateRestriction("minimum", "remove")}>
          {formatMessage("removeMinimums")}
        </MenuItem>
      ) : (
        <MenuItem key="addMinimums" onClick={createHandleUpdateRestriction("minimum", "add")}>
          {formatMessage("addMinimums")}
        </MenuItem>
      ),
      useMaximums ? (
        <MenuItem key="removeMaximums" onClick={createHandleUpdateRestriction("maximum", "remove")}>
          {formatMessage("removeMaximums")}
        </MenuItem>
      ) : (
        <MenuItem key="addMaximums" onClick={createHandleUpdateRestriction("maximum", "add")}>
          {formatMessage("addMaximums")}
        </MenuItem>
      ),
    ];
  }, [createHandleUpdateRestriction, useMinimums, useMaximums]);

  const isPositioned = cssProperties.position === "absolute" || cssProperties.position === "fixed";

  const handleAbsolutePositionToggle = () => {
    if (isPositioned) {
      onChange({
        cssProperties: {
          position: undefined,
          ...clearAllInsetProperties(),
        },
        layoutSettings: { offsetOrigin: undefined },
      });
    } else {
      onChange({
        cssProperties: {
          position: "absolute",
          top: "0px",
          left: "0px",
        },
        layoutSettings: { offsetOrigin: "topLeft" },
      });
    }
  };

  return (
    <PropertiesPanelSection
      title={formatMessage("sectionHeading")}
      controls={
        <>
          <Tooltip title={formatMessage("absolutePosition")} placement="top">
            <StyledToggleButton
              value="absolute-position"
              selected={isPositioned}
              onChange={handleAbsolutePositionToggle}
              color="primary"
            >
              <SkiliticsIcon icon="absolute-position" />
            </StyledToggleButton>
          </Tooltip>
          <ContextMenu data-test="layout-properties-context-menu">{menuItems}</ContextMenu>
        </>
      }
      data-test="location-and-size-properties-section"
    >
      <SizeForm
        isWidthInactive={layoutSettings.horizontalBehavior !== "fixed"}
        isHeightInactive={layoutSettings.verticalBehavior !== "fixed"}
        useMinimums={useMinimums}
        useMaximums={useMaximums}
      />
      <LayoutSettingsForm
        layoutSettings={layoutSettings}
        onLayoutSettingsChange={onLayoutSettingsChange}
        enableFitContent={enableFitContent}
      />
      {isPositioned && <AbsolutePositionForm sliderDirection={sliderDirection} />}
    </PropertiesPanelSection>
  );
};

const formatStrings = {
  sectionHeading: {
    en: "Location & Size",
  },
  addMinimums: {
    en: "Add Minimums",
  },
  removeMinimums: {
    en: "Remove Minimums",
  },
  addMaximums: {
    en: "Add Maximums",
  },
  removeMaximums: {
    en: "Remove Maximums",
  },
  absolutePosition: {
    en: "Absolute position element relative to its parent",
  },
} satisfies FormatMessages;
